import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Search, Plus, Download, MoreHorizontal, Eye, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin } from '@/lib/auth';
import api from '@/lib/api';

const DEPARTMENTS = ['All Departments', 'Directors', 'Sales', 'Accounts', 'BackOffice', 'Site', 'Purchase', 'Factory', 'Office Support', 'Godown Incharge'];

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('All Departments');
  const [statusFilter, setStatusFilter] = useState('active');
  const [addOpen, setAddOpen] = useState(false);
  const [newEmp, setNewEmp] = useState({ name: '', designation: '', department: 'Sales', email: '', phone: '' });
  const navigate = useNavigate();
  const admin = isAdmin();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/employees', { params: { department: department === 'All Departments' ? undefined : department, status: statusFilter, search: search || undefined } });
      setEmployees(data.employees);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [department, statusFilter, search]);

  const handleAdd = async () => {
    if (!newEmp.name || !newEmp.designation || !newEmp.department) { toast.error('Fill required fields'); return; }
    try {
      await api.post('/api/employees', newEmp);
      toast.success('Employee added');
      setAddOpen(false);
      setNewEmp({ name: '', designation: '', department: 'Sales', email: '', phone: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const initials = (name) => name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// EMPLOYEES</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Employee Directory</h1>
          <p className="text-sm text-slate-500">{total} employees in the organization</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
          {admin && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246]" data-testid="employee-add-button"><Plus className="w-4 h-4 mr-1" />Add Employee</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add New Employee</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div><Label>Full Name *</Label><Input value={newEmp.name} onChange={e => setNewEmp({ ...newEmp, name: e.target.value })} data-testid="add-emp-name" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Designation *</Label><Input value={newEmp.designation} onChange={e => setNewEmp({ ...newEmp, designation: e.target.value })} data-testid="add-emp-designation" /></div>
                    <div><Label>Department *</Label>
                      <Select value={newEmp.department} onValueChange={v => setNewEmp({ ...newEmp, department: v })}>
                        <SelectTrigger data-testid="add-emp-dept"><SelectValue /></SelectTrigger>
                        <SelectContent>{DEPARTMENTS.filter(d => d !== 'All Departments').map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Email</Label><Input value={newEmp.email} onChange={e => setNewEmp({ ...newEmp, email: e.target.value })} data-testid="add-emp-email" /></div>
                    <div><Label>Phone</Label><Input value={newEmp.phone} onChange={e => setNewEmp({ ...newEmp, phone: e.target.value })} data-testid="add-emp-phone" /></div>
                  </div>
                  <Button onClick={handleAdd} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="add-emp-submit">Add Employee</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="employee-search-input" />
          </div>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger className="w-[200px] h-9" data-testid="employee-directory-filter-department"><SelectValue /></SelectTrigger>
            <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[120px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 rounded" />)}</div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Employee</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider hidden md:table-cell">Department</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider hidden lg:table-cell">Type</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider hidden sm:table-cell">Contact</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => (
                  <TableRow key={emp.id} className="hover:bg-[#F7FAFF] cursor-pointer" onClick={() => navigate(`/dashboard/employees/${emp.id}`)} data-testid={`employee-row-${emp.employee_code}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9"><AvatarFallback className="bg-[#0D2850] text-white text-xs font-bold">{initials(emp.name)}</AvatarFallback></Avatar>
                        <div>
                          <p className="text-sm font-medium text-[#0B1B33]">{emp.name}</p>
                          <p className="text-xs text-slate-400">{emp.employee_code}</p>
                          <p className="text-xs text-slate-400 sm:hidden">{emp.department}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell"><span className="text-sm text-slate-600">{emp.department}</span></TableCell>
                    <TableCell className="hidden lg:table-cell"><span className="text-sm text-slate-600">{emp.employment_type}</span></TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="text-xs text-slate-500">
                        {emp.email && <p className="truncate max-w-[180px]">{emp.email}</p>}
                        {emp.phone && <p>{emp.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={emp.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>{emp.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/employees/${emp.id}`); }}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>
                          {admin && <DropdownMenuItem className="text-red-600" onClick={e => e.stopPropagation()}><UserX className="w-4 h-4 mr-2" />Deactivate</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
